import { useNavigate, useParams } from 'react-router-dom'
import classNames from 'classnames'
import useSetPlan from './hooks/useSetPlan'
import PlanDetail from './components/PlanDetail'
import { PLAN_SUBSCRIPTION, PLAN_TIERS, getTier } from './utils'
import { faPlain } from 'utilities/helper/format'
import Header from 'components/headers/bulletins'
import { toggleSupportModal } from 'store/redux/sidebar'
import { useDispatch, useSelector } from 'react-redux'
import SupportModal from 'components/modals/supportModal'
import Portal from 'components/portal'
import { RootState } from 'store/redux'
import useGetMoneyPoolManagement from 'pages/fund/fundPage/hooks/useGetMoneyPoolManagement'
import useGetPrice from 'pages/fund/subscription/hooks/useGetPrice'

export default function BuySubscription() {
    const { moneyPool } = useParams()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { isOpenSupportModal } = useSelector(
        (state: RootState) => state.sideBarControler
    )

    const { moneyPoolManagement } = useGetMoneyPoolManagement(moneyPool)
    const currentPackage = moneyPoolManagement?.service_package
    const { selected, selectPlan } = useSetPlan(currentPackage)

    // The subscription price scales with the fund's member count, so it can't
    // be a constant in the selector — the old page's hardcoded figures were
    // stale by 4×.
    const { price, isLoading: isPriceLoading } = useGetPrice(moneyPool, 1)

    const selectedTier = selected !== null ? getTier(selected) : null
    const isCurrent = selected !== null && selected === currentPackage

    // A live subscription can always be extended. The backend stacks the
    // periods rather than restarting the clock — `calculate_order_end_date`
    // adds the days still left on the current order onto the new one — so
    // buying early costs the manager nothing. Blocking it here was the whole
    // bug: the fund's own tier came back pre-selected, hit the «this version is
    // already active» branch, and the manager had no way to pay until the
    // subscription had already lapsed. The Android app never had this wall.
    const remainingDays = moneyPoolManagement?.service_package_remaining_days
    const isRenewal = isCurrent && selected === PLAN_SUBSCRIPTION
    const canBuy = !!selectedTier?.route && (!isCurrent || isRenewal)

    const priceFor = (servicePackage: number) => {
        const tier = getTier(servicePackage)
        if (servicePackage !== PLAN_SUBSCRIPTION) return tier.priceLabel
        if (price) return `${price.toLocaleString('fa-IR')} تومان`
        return isPriceLoading ? '…' : '—'
    }

    return (
        <>
            <div className=" flex min-h-screen flex-col bg-canvas">
                <div className=" grow-0">
                    <Header title="ارتقاء صندوق" />
                </div>

                {/* pb-28 clears the pinned CTA below — without it the
                    «با مشاور همیان تماس بگیرید» link sits under the bar. */}
                <div className=" flex grow flex-col px-4 pb-28">
                    <p className=" mt-4 text-center text-sm leading-6 text-text-secondary">
                        یکی از سه نسخه‌ی زیر را برای صندوق انتخاب کنید
                    </p>

                    {/* Tier selector. Each tab carries its own cost, so the
                        comparison happens here rather than one screen later. */}
                    <div
                        role="radiogroup"
                        aria-label="نسخه‌های صندوق"
                        className=" mt-4 grid grid-cols-3 gap-2"
                    >
                        {PLAN_TIERS.map((tier) => {
                            const isSelected = selected === tier.servicePackage
                            const isActivePlan =
                                currentPackage === tier.servicePackage
                            return (
                                <button
                                    key={tier.servicePackage}
                                    type="button"
                                    role="radio"
                                    aria-checked={isSelected}
                                    onClick={() =>
                                        selectPlan(tier.servicePackage)
                                    }
                                    className={classNames(
                                        'relative flex flex-col items-center justify-start gap-1 rounded-card border px-2 py-3 text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                                        isSelected
                                            ? ' border-brand bg-brand-soft'
                                            : ' border-stroke bg-white'
                                    )}
                                >
                                    {isActivePlan && (
                                        <span className=" absolute -top-2 rounded-full bg-brand px-2 py-0.5 text-[10px] font-bold text-white">
                                            فعلی
                                        </span>
                                    )}
                                    <span
                                        className={classNames(
                                            'mt-1 text-sm font-bold',
                                            isSelected
                                                ? ' text-brand-deep'
                                                : ' text-text-body'
                                        )}
                                    >
                                        {tier.name}
                                    </span>
                                    <span
                                        className={classNames(
                                            'text-[11px] leading-4',
                                            isSelected
                                                ? ' text-brand-deep'
                                                : ' text-text-secondary'
                                        )}
                                    >
                                        {priceFor(tier.servicePackage)}
                                    </span>
                                </button>
                            )
                        })}
                    </div>

                    <div className=" mt-4">
                        {selectedTier ? (
                            <PlanDetail
                                key={selectedTier.servicePackage}
                                tier={selectedTier}
                                price={price}
                                isPriceLoading={isPriceLoading}
                            />
                        ) : (
                            <div className=" h-56 animate-pulse rounded-sheet bg-white shadow-card" />
                        )}
                    </div>

                    <div className=" mt-auto flex flex-col items-center pb-4 pt-8 text-center">
                        <p className=" text-xs text-text-secondary">
                            نیاز به راهنمایی دارید؟
                        </p>
                        <button
                            type="button"
                            className=" mt-1 text-sm font-bold text-brand-deep underline underline-offset-4"
                            onClick={() =>
                                dispatch(
                                    toggleSupportModal({
                                        isOpenSupportModal: true,
                                    })
                                )
                            }
                        >
                            با مشاور همیان تماس بگیرید
                        </button>
                    </div>
                </div>
            </div>

            {/* Pinned, and portalled to <body> to get there.
                `mt-auto` in a min-h-screen column only puts the CTA at the
                bottom of the PAGE, so on a tall plan card it fell below the
                fold — the plan you are choosing was visible but the button to
                choose it was not. And a plain `fixed` would not have helped:
                FundLayout keeps a framer-motion transform on every route,
                which becomes the containing block for fixed descendants. */}
            <Portal>
                {/* Two elements on purpose: the outer one positions and
                    reproduces FundLayout's desktop phone frame, the inner one
                    IS the bar. With both on one element the white surface
                    stretched the full viewport on desktop while its contents
                    sat in the middle third — a white band across the gutters. */}
                <div className=" fixed bottom-0 left-0 right-0 z-40 md:px-[35%]">
                    <div className=" border-t border-divider bg-white px-4 py-3">
                        {canBuy ? (
                            <>
                                {isRenewal && !!remainingDays && (
                                    <p className=" mb-2 text-center text-xs leading-5 text-text-secondary">
                                        {faPlain.format(remainingDays)} روز از
                                        اشتراک فعلی باقی مانده و به دوره‌ی تازه
                                        اضافه می‌شود
                                    </p>
                                )}
                                <button
                                    type="button"
                                    className=" w-full rounded-card bg-brand py-3 text-sm font-bold text-white transition-colors active:bg-brand-strong"
                                    onClick={() =>
                                        navigate(
                                            `/fund/plans/${
                                                selectedTier!.route
                                            }/${moneyPool}`
                                        )
                                    }
                                >
                                    {isRenewal
                                        ? 'تمدید اشتراک'
                                        : `انتخاب نسخه‌ی ${selectedTier!.name}`}
                                </button>
                            </>
                        ) : isCurrent ? (
                            <p className=" py-2 text-center text-sm text-text-secondary">
                                این نسخه هم‌اکنون روی صندوق فعال است
                            </p>
                        ) : (
                            <p className=" py-2 text-center text-sm text-text-secondary">
                                نسخه‌ی رایگان نیازی به فعال‌سازی ندارد
                            </p>
                        )}
                    </div>
                </div>
            </Portal>

            <SupportModal
                show={isOpenSupportModal}
                onHide={() =>
                    dispatch(toggleSupportModal({ isOpenSupportModal: false }))
                }
            />
        </>
    )
}
